// Custom fetch mutator for the orval-generated control-plane client.
//
// The generated request functions call `customFetch<T>(url, init)` for every
// operation. We keep it thin and same-origin: the browser talks only to its own
// origin, and the Node/Vite reverse proxy forwards `/v1/*` to the control plane
// while attaching the session bearer - so no Authorization header is set here.
//
// The one behaviour we preserve from the retired `cp.ts` is FastAPI error
// unwrapping: a `{detail: "..."}` string or a validation `[{msg}]` array becomes
// a thrown `Error(message)`, so react-query surfaces it as `query.error` /
// `mutation.error` with a human-readable message instead of a bare status code.

/**
 * A failed control-plane response. Carries the status so a caller can branch on
 * it (403 -> not authorized) instead of matching on the message; `message` keeps
 * the unwrapped FastAPI detail, so existing callers are unaffected.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly detail?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function fail(res: Response): Promise<never> {
  const body = (await res.json().catch(() => ({}))) as { detail?: unknown };
  const detail = body.detail;
  if (typeof detail === "string") throw new ApiError(detail, res.status);
  // The jobs API names each bad field as one "field: reason" string.
  if (Array.isArray(detail) && detail.length && detail.every((issue) => typeof issue === "string")) {
    throw new ApiError(detail.join("; "), res.status, detail);
  }
  if (
    Array.isArray(detail) &&
    detail[0] &&
    typeof detail[0] === "object" &&
    "msg" in detail[0]
  ) {
    throw new ApiError(String((detail[0] as { msg: unknown }).msg), res.status, detail);
  }
  throw new ApiError(`HTTP ${res.status}`, res.status);
}

export async function customFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) return fail(res);
  // 204 (DELETE) and empty bodies have no JSON to parse - resolve to undefined.
  if (res.status === 204 || res.headers.get("content-length") === "0") {
    return undefined as T;
  }
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}
